// PLAN-cockpit-v2-domain-lenses Phase 2: Finance Lens
// Main: finance timeline (domain=finance) + WatchList panel
// Sidebar: period filter + type toggle

import { useEffect, useMemo, useState } from 'react'
import { apiFetch, UnauthorizedError } from '../../api/client'
import { listOperationalData, upsertOperationalData } from '../../api/kdb'
import { ResultRow } from '../components/common/ResultRow'
import { EmptyState } from '../components/common/EmptyState'
import { useSelectedItem } from '../hooks/useSelectedItem'
import type { SearchHit, SearchResponse } from '../../types'

type Preset = 'week' | 'month' | 'quarter' | 'year'
type ItemFilter = 'all' | 'entries' | 'articles'

function rangeOf(p: Preset): { since: string; until: string; label: string } {
  const now = new Date()
  const until = now.toISOString()
  const past = new Date(now)
  if (p === 'week') past.setDate(now.getDate() - 7)
  if (p === 'month') past.setMonth(now.getMonth() - 1)
  if (p === 'quarter') past.setMonth(now.getMonth() - 3)
  if (p === 'year') past.setFullYear(now.getFullYear() - 1)
  return {
    since: past.toISOString(),
    until,
    label: p === 'week' ? 'Past 7 days' : p === 'month' ? 'Past 30 days' : p === 'quarter' ? 'Past 90 days' : 'Past year',
  }
}

// ===== Sidebar =====

export function FinanceSidebar() {
  const [preset, setPreset] = useState<Preset>(
    () => (window.sessionStorage.getItem('finance_preset') as Preset) ?? 'month'
  )
  const [itemFilter, setItemFilter] = useState<ItemFilter>(
    () => (window.sessionStorage.getItem('finance_item_filter') as ItemFilter) ?? 'all'
  )

  const update = (p: Preset, f: ItemFilter) => {
    setPreset(p)
    setItemFilter(f)
    window.sessionStorage.setItem('finance_preset', p)
    window.sessionStorage.setItem('finance_item_filter', f)
    window.dispatchEvent(new CustomEvent('finance_filter_change'))
  }

  return (
    <div className="p-4 space-y-6 text-sm">
      <div>
        <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-3">Period</h2>
        <div className="flex flex-col gap-1.5">
          {(['week', 'month', 'quarter', 'year'] as Preset[]).map(p => (
            <button key={p} type="button"
              onClick={() => update(p, itemFilter)}
              className={`text-left text-sm px-2 py-1.5 rounded ${preset === p ? 'bg-bg-overlay text-text-base' : 'text-text-muted hover:bg-bg-overlay'}`}>
              {p === 'week' ? '7 days' : p === 'month' ? '30 days' : p === 'quarter' ? '90 days' : '1 year'}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-3">Show</h2>
        <div className="flex flex-col gap-1.5">
          {([
            { v: 'all', label: 'All' },
            { v: 'entries', label: 'Entries only' },
            { v: 'articles', label: 'Articles only' },
          ] as { v: ItemFilter; label: string }[]).map(({ v, label }) => (
            <button key={v} type="button"
              onClick={() => update(preset, v)}
              className={`text-left text-sm px-2 py-1.5 rounded ${itemFilter === v ? 'bg-bg-overlay text-text-base' : 'text-text-muted hover:bg-bg-overlay'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ===== Main =====

export function FinanceLens() {
  const [preset, setPreset] = useState<Preset>(
    () => (window.sessionStorage.getItem('finance_preset') as Preset) ?? 'month'
  )
  const [itemFilter, setItemFilter] = useState<ItemFilter>(
    () => (window.sessionStorage.getItem('finance_item_filter') as ItemFilter) ?? 'all'
  )
  const { setSelectedItem } = useSelectedItem()
  const [timeline, setTimeline] = useState<SearchHit[]>([])
  const [tlLoading, setTlLoading] = useState(false)
  const [kpiTotal, setKpiTotal] = useState<number | null>(null)

  useEffect(() => {
    const handler = () => {
      setPreset((window.sessionStorage.getItem('finance_preset') as Preset) ?? 'month')
      setItemFilter((window.sessionStorage.getItem('finance_item_filter') as ItemFilter) ?? 'all')
    }
    window.addEventListener('finance_filter_change', handler)
    return () => window.removeEventListener('finance_filter_change', handler)
  }, [])

  const range = useMemo(() => rangeOf(preset), [preset])

  useEffect(() => {
    const ac = new AbortController()
    setTlLoading(true)
    apiFetch<SearchResponse>('/api/search', {
      query: { domain: 'finance', domain_mode: 'broad', since: range.since, until: range.until, sort: '-created_at', limit: 200 },
      signal: ac.signal,
    })
      .then(res => { setTimeline(res.entries ?? []); setKpiTotal(res.total ?? 0) })
      .catch(() => { setTimeline([]); setKpiTotal(null) })
      .finally(() => setTlLoading(false))
    return () => ac.abort()
  }, [range.since, range.until])

  const filteredTimeline = useMemo(() => {
    if (itemFilter === 'all') return timeline
    if (itemFilter === 'entries') return timeline.filter(h => h.origin !== 'synthesized')
    if (itemFilter === 'articles') return timeline.filter(h => h.origin === 'synthesized')
    return timeline
  }, [timeline, itemFilter])

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-3xl mx-auto w-full px-8 py-10 space-y-10">
        <header className="space-y-3">
          <p className="text-xs uppercase tracking-wide text-text-subtle">Finance · {range.label}</p>
          <h1 className="text-2xl font-medium text-text-base">Finance</h1>
          <p className="text-sm text-text-muted">
            <span className="font-medium">{kpiTotal ?? '—'}</span> entries / articles
          </p>
        </header>

        {/* WatchList */}
        <section>
          <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-4">Watchlist</h2>
          <WatchlistPanel />
        </section>

        {/* Timeline */}
        <section>
          <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-1">Timeline</h2>
          {itemFilter !== 'all' && (
            <p className="text-xs text-text-subtle mb-3">Filter: {itemFilter}</p>
          )}
          {tlLoading ? (
            <p className="text-sm text-text-subtle">Loading…</p>
          ) : filteredTimeline.length === 0 ? (
            <EmptyState title="No finance items in this period" description="期間を変更するか entry を追加してください" />
          ) : (
            <ul className="border-t border-border-subtle">
              {filteredTimeline.map(hit => {
                const isArticle = hit.origin === 'synthesized'
                const itemType: 'entry' | 'article' = isArticle ? 'article' : 'entry'
                return (
                  <li key={hit.id}>
                    <ResultRow
                      id={hit.id}
                      title={hit.title}
                      one_line={hit.one_line}
                      origin={hit.origin}
                      subjectivity={hit.subjectivity}
                      source_project={hit.source_project ?? null}
                      domains={hit.domains ?? []}
                      visibility={hit.visibility ?? null}
                      has_decision={hit.has_decision}
                      x_status={hit.x_status ?? null}
                      created_at={hit.created_at}
                      onClick={() => setSelectedItem({ id: hit.id, itemType })}
                    />
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}

// ===== WatchList panel (v1 FinanceDashboardCard から移植) =====

interface MarketPrice {
  symbol: string
  trade_date: string
  open_price: number | null
  high_price: number | null
  low_price: number | null
  close_price: number
  volume: number | null
  source: string | null
  ingested_at: string
}

function todayISO(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function fmtNum(n: number | null | undefined, digits = 2): string {
  if (n == null) return '—'
  return n.toLocaleString('ja-JP', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

function WatchlistPanel() {
  const [prices, setPrices] = useState<MarketPrice[]>([])
  const [loading, setLoading] = useState(true)
  const [reload, setReload] = useState(0)
  const [symbol, setSymbol] = useState('')
  const [tradeDate, setTradeDate] = useState(todayISO())
  const [closePrice, setClosePrice] = useState('')
  const [openPrice, setOpenPrice] = useState('')
  const [highPrice, setHighPrice] = useState('')
  const [lowPrice, setLowPrice] = useState('')
  const [volume, setVolume] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitMsg, setSubmitMsg] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listOperationalData<MarketPrice>('finance_market_prices', { limit: 200 })
      .then(res => { if (!cancelled) { setPrices(res.rows); setLoading(false) } })
      .catch((e: unknown) => {
        if (!cancelled) {
          if (e instanceof UnauthorizedError) return
          setLoading(false)
        }
      })
    return () => { cancelled = true }
  }, [reload])

  const watchlist = useMemo(() => {
    const bySymbol = new Map<string, MarketPrice>()
    for (const p of prices) {
      const ex = bySymbol.get(p.symbol)
      if (!ex || p.trade_date > ex.trade_date) bySymbol.set(p.symbol, p)
    }
    return Array.from(bySymbol.values()).sort((a, b) => a.symbol.localeCompare(b.symbol))
  }, [prices])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setSubmitMsg(null)
    try {
      const body: Record<string, unknown> = {
        symbol: symbol.trim().toUpperCase(),
        trade_date: tradeDate,
        close_price: parseFloat(closePrice),
        source: 'manual',
      }
      if (openPrice) body.open_price = parseFloat(openPrice)
      if (highPrice) body.high_price = parseFloat(highPrice)
      if (lowPrice) body.low_price = parseFloat(lowPrice)
      if (volume) body.volume = parseInt(volume, 10)
      await upsertOperationalData<MarketPrice>('finance_market_prices', body)
      setSubmitMsg(`保存: ${symbol.trim().toUpperCase()} @ ${tradeDate}`)
      setReload(n => n + 1)
      setClosePrice(''); setOpenPrice(''); setHighPrice(''); setLowPrice(''); setVolume('')
    } catch (e: unknown) {
      setSubmitMsg(`保存失敗: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="border border-border-subtle rounded-lg overflow-hidden">
      {/* 銘柄一覧 */}
      <div className="overflow-x-auto">
        {loading ? (
          <p className="px-4 py-3 text-sm text-text-subtle">Loading…</p>
        ) : watchlist.length === 0 ? (
          <p className="px-4 py-3 text-sm text-text-subtle">銘柄データがありません。下のフォームから追加してください。</p>
        ) : (
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-border-subtle text-text-subtle">
                <th className="text-left px-4 py-2">symbol</th>
                <th className="text-left px-2 py-2">date</th>
                <th className="text-right px-2 py-2">close</th>
                <th className="text-right px-2 py-2">open</th>
                <th className="text-right px-2 py-2">high</th>
                <th className="text-right px-2 py-2">low</th>
                <th className="text-right px-4 py-2">volume</th>
              </tr>
            </thead>
            <tbody>
              {watchlist.map(p => (
                <tr key={p.symbol} className="border-b border-border-subtle hover:bg-bg-overlay">
                  <td className="px-4 py-2 font-mono font-semibold text-text-base">{p.symbol}</td>
                  <td className="px-2 py-2 font-mono text-text-muted">{p.trade_date}</td>
                  <td className="px-2 py-2 text-right tabular-nums font-semibold text-text-base">{fmtNum(p.close_price)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-text-muted">{fmtNum(p.open_price)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-text-muted">{fmtNum(p.high_price)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-text-muted">{fmtNum(p.low_price)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-text-subtle">{p.volume?.toLocaleString() ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 入力フォーム (折り畳み) */}
      <div className="border-t border-border-subtle">
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="w-full flex items-center justify-between px-4 py-2.5 text-xs text-text-subtle hover:bg-bg-overlay"
        >
          <span>価格を記録</span>
          <span>{expanded ? '▲' : '▼'}</span>
        </button>
        {expanded && (
          <form onSubmit={handleSubmit} className="px-4 pb-4 space-y-3 bg-bg-surface">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { label: '銘柄 *', value: symbol, set: setSymbol, placeholder: '7203.T / AAPL', required: true, type: 'text', mono: true },
                { label: '日付 *', value: tradeDate, set: setTradeDate, required: true, type: 'date' },
                { label: '終値 *', value: closePrice, set: setClosePrice, placeholder: '2500', required: true, type: 'number' },
                { label: '出来高', value: volume, set: setVolume, placeholder: '1000000', required: false, type: 'number' },
                { label: '始値', value: openPrice, set: setOpenPrice, required: false, type: 'number' },
                { label: '高値', value: highPrice, set: setHighPrice, required: false, type: 'number' },
                { label: '安値', value: lowPrice, set: setLowPrice, required: false, type: 'number' },
              ].map(({ label, value, set, placeholder, required, type, mono }) => (
                <label key={label} className="flex flex-col gap-1">
                  <span className="text-[11px] text-text-subtle">{label}</span>
                  <input
                    type={type}
                    step={type === 'number' && !label.includes('出来高') ? '0.01' : undefined}
                    value={value}
                    onChange={e => set(e.target.value)}
                    placeholder={placeholder}
                    required={required}
                    className={`px-2 py-1.5 bg-bg-base border border-border-subtle rounded text-sm text-text-base placeholder:text-text-subtle focus:outline-none focus:border-border-strong ${mono ? 'font-mono' : ''}`}
                  />
                </label>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="px-3 py-1.5 bg-accent-primary text-bg-surface rounded text-sm hover:opacity-90 disabled:opacity-60"
              >
                {submitting ? '保存中…' : '保存'}
              </button>
              <button type="button" onClick={() => setReload(n => n + 1)}
                className="px-3 py-1.5 bg-bg-overlay border border-border-subtle rounded text-sm text-text-base hover:bg-border-subtle">
                再読込
              </button>
              {submitMsg && <span className="text-xs text-text-muted">{submitMsg}</span>}
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
